import React, { useEffect, useMemo, useState } from 'react';
import {
  FACET_ROW_META,
  hydrateFacetsFromLegacyTags,
  normalizeCameraList,
  normalizeStringList,
} from './tagConfigUtils';

function facetSignature(facets) {
  return FACET_ROW_META.map(({ key }) => `${key}:${(facets[key] || []).join('\u0001')}`).join('\u0002');
}

export default function LightboxTagEditor({
  photo,
  facetOptions,
  canEdit,
  saving,
  onSave,
}) {
  const hydrated = useMemo(
    () => hydrateFacetsFromLegacyTags(photo?.metadata),
    [photo]
  );
  const signature = facetSignature(hydrated);
  const [draft, setDraft] = useState(hydrated);
  const [facetKey, setFacetKey] = useState('people');
  const [query, setQuery] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    setDraft(hydrateFacetsFromLegacyTags(photo?.metadata));
    setQuery('');
    setError('');
  }, [photo?.path, signature]); // eslint-disable-line react-hooks/exhaustive-deps

  const facetMeta = FACET_ROW_META.find((row) => row.key === facetKey) || FACET_ROW_META[0];
  const suggestions = useMemo(() => {
    const selected = new Set(normalizeStringList(draft[facetKey]));
    const needle = query.trim().toLowerCase();
    return normalizeStringList(facetOptions?.[facetKey])
      .filter((name) => !selected.has(name))
      .filter((name) => !needle || name.toLowerCase().includes(needle))
      .slice(0, 8);
  }, [draft, facetKey, facetOptions, query]);

  const commit = async (next) => {
    setDraft(next);
    setError('');
    const ok = await onSave(next);
    if (!ok) setError('Could not save that tag.');
  };

  const addValue = (raw) => {
    const value = String(raw || '').trim();
    if (!value || saving) return;
    const current = normalizeStringList(draft[facetKey]);
    if (current.some((item) => item.toLowerCase() === value.toLowerCase())) {
      setQuery('');
      return;
    }
    const nextValues = facetMeta.mode === 'single' ? normalizeCameraList([value]) : [...current, value];
    setQuery('');
    commit({ ...draft, [facetKey]: nextValues });
  };

  const removeValue = (key, value) => {
    if (saving) return;
    commit({
      ...draft,
      [key]: normalizeStringList(draft[key]).filter((item) => item !== value),
    });
  };

  const groups = FACET_ROW_META.map((row) => ({
    ...row,
    values: normalizeStringList(draft[row.key]),
  })).filter((row) => row.values.length > 0);

  return (
    <div className="lightbox-tag-editor">
      <p className="lightbox-filename">{photo?.metadata?.originalFilename || photo?.name || 'Photo'}</p>
      <h2 className="lightbox-tags-heading">Tags</h2>
      {groups.length === 0 ? (
        <p className="lightbox-tags-empty">No tags yet.</p>
      ) : (
        <div className="lightbox-tag-groups">
          {groups.map((row) => (
            <section key={row.key} className="lightbox-tag-group" aria-label={row.label}>
              <h3 className="lightbox-tag-group-label">{row.label}</h3>
              <div className="lightbox-tag-pills">
                {row.values.map((value) => (
                  <span key={`${row.key}:${value}`} className="lightbox-tag-chip">
                    <span>{value}</span>
                    {canEdit && (
                      <button
                        type="button"
                        className="lightbox-tag-chip-remove"
                        aria-label={`Remove ${row.label} ${value}`}
                        disabled={saving}
                        onClick={() => removeValue(row.key, value)}
                      >
                        ×
                      </button>
                    )}
                  </span>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
      {canEdit && (
        <form
          className="lightbox-tag-add"
          onSubmit={(event) => {
            event.preventDefault();
            addValue(query);
          }}
        >
          <label className="lightbox-tag-add-label" htmlFor="lightbox-tag-facet">
            Add
          </label>
          <div className="lightbox-tag-add-row">
            <select
              id="lightbox-tag-facet"
              className="lightbox-tag-select"
              value={facetKey}
              disabled={saving}
              onChange={(event) => {
                setFacetKey(event.target.value);
                setQuery('');
              }}
            >
              {FACET_ROW_META.map((row) => (
                <option key={row.key} value={row.key}>
                  {row.label}
                </option>
              ))}
            </select>
            <input
              className="lightbox-tag-input"
              value={query}
              disabled={saving}
              placeholder={`Add ${facetMeta.label.toLowerCase()}`}
              aria-label={`Add ${facetMeta.label}`}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  event.stopPropagation();
                  setQuery('');
                }
              }}
            />
            <button className="lightbox-tag-submit" type="submit" disabled={saving || !query.trim()}>
              {saving ? 'Saving' : 'Add'}
            </button>
          </div>
          {query.trim() && suggestions.length > 0 && (
            <ul className="lightbox-tag-suggestions" aria-label="Matching tags">
              {suggestions.map((name) => (
                <li key={name}>
                  <button type="button" disabled={saving} onClick={() => addValue(name)}>
                    {name}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {error && <p className="lightbox-tag-error">{error}</p>}
        </form>
      )}
    </div>
  );
}
